/**
 * Bible Lesson Management Module
 * Allows admins to upload PDF schedules, bulk-import, and manually manage Bible lessons
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Upload, BookOpen, Plus, Trash2, Calendar, FileText, Download,
  Loader2, ChevronDown, ChevronUp, CheckCircle2, AlertCircle, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPES = [
  'Sunday Morning Service',
  'Sunday Evening Service',
  'Mercy Day Service',
  'Friday Power Service',
  'New Moon Service',
  'Bible Study',
];

const BibleLessonManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/bible-lessons-manage/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  const loadLessons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/bible-lessons-manage/upcoming?limit=200`);
      setLessons(res.data || []);
    } catch (error) {
      console.error('Error loading lessons:', error);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadLessons();
  }, [loadStats, loadLessons]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'upload', label: 'Upload PDF', icon: Upload },
    { id: 'manual', label: 'Add Lessons', icon: Plus },
    { id: 'browse', label: 'Browse', icon: BookOpen },
  ];

  return (
    <div className="space-y-6" data-testid="bible-lesson-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Bible Lesson Schedule</h2>
            <p className="text-sm text-slate-500">Upload, manage, and track Bible lessons</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
              activeTab === tab.id
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <OverviewPanel stats={stats} onRefresh={loadStats} />
          </motion.div>
        )}
        {activeTab === 'upload' && (
          <motion.div key="upload" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <PDFUploadPanel onImportComplete={() => { loadStats(); loadLessons(); }} />
          </motion.div>
        )}
        {activeTab === 'manual' && (
          <motion.div key="manual" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ManualEntryPanel onLessonAdded={() => { loadStats(); loadLessons(); }} />
          </motion.div>
        )}
        {activeTab === 'browse' && (
          <motion.div key="browse" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <BrowseLessonsPanel lessons={lessons} onDelete={() => { loadStats(); loadLessons(); }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== OVERVIEW PANEL ====================
const OverviewPanel = ({ stats, onRefresh }) => {
  if (!stats) return <div className="text-center py-8 text-slate-400">Loading stats...</div>;

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Lessons" value={stats.total_lessons} color="bg-blue-50 text-blue-700" />
        <StatCard label="Upcoming" value={stats.upcoming_lessons} color="bg-green-50 text-green-700" />
        <StatCard label="Past" value={stats.past_lessons} color="bg-slate-50 text-slate-600" />
        <StatCard label="Date Range" value={stats.date_range?.earliest ? `${stats.date_range.earliest.slice(5)} to ${stats.date_range.latest?.slice(5) || '?'}` : 'None'} color="bg-purple-50 text-purple-700" small />
      </div>

      {/* By Service Type */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Lessons by Service Type</h3>
        <div className="space-y-2">
          {stats.by_service_type?.map(s => (
            <div key={s.service_type} className="flex items-center justify-between py-1.5">
              <span className="text-sm text-slate-600">{s.service_type}</span>
              <span className="text-sm font-semibold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-full">{s.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* By Source */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Import Sources</h3>
        <div className="space-y-2">
          {stats.by_source?.map(s => (
            <div key={s.source} className="flex items-center justify-between py-1.5">
              <span className="text-sm text-slate-600">{s.source}</span>
              <span className="text-sm font-semibold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-full">{s.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, small }) => (
  <div className={`rounded-xl p-3 ${color}`}>
    <p className={`font-bold ${small ? 'text-sm' : 'text-xl'}`}>{value}</p>
    <p className="text-xs opacity-70">{label}</p>
  </div>
);

// ==================== PDF UPLOAD PANEL ====================
const PDFUploadPanel = ({ onImportComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [editableLessons, setEditableLessons] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setResult(null);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/bible-lessons-manage/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      setEditableLessons(res.data.auto_parsed_lessons || []);
    } catch (error) {
      alert(error.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleImport = async () => {
    if (editableLessons.length === 0) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/api/bible-lessons-manage/bulk-import`, {
        lessons: editableLessons
      });
      setImportResult(res.data);
      onImportComplete();
    } catch (error) {
      alert(error.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  const updateLesson = (idx, field, value) => {
    const updated = [...editableLessons];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditableLessons(updated);
  };

  const removeLesson = (idx) => {
    setEditableLessons(editableLessons.filter((_, i) => i !== idx));
  };

  const addEmptyLesson = () => {
    setEditableLessons([...editableLessons, { service_type: 'Sunday Morning Service', date: '', bible_lesson: '' }]);
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors">
        <input ref={fileRef} type="file" accept=".pdf" onChange={handleUpload} className="hidden" />
        <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
        <p className="text-slate-600 font-medium mb-1">Upload Bible Lesson Schedule PDF</p>
        <p className="text-sm text-slate-400 mb-4">The system will extract text and try to auto-parse lessons</p>
        <button
          data-testid="upload-pdf-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          {uploading ? 'Processing...' : 'Select PDF File'}
        </button>
      </div>

      {/* Upload Result */}
      {result && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="w-5 h-5" />
            <p className="font-medium">PDF processed: {result.pages} pages, {result.auto_parsed_count} lessons found</p>
          </div>
          <p className="text-sm text-slate-500">{result.message}</p>

          {/* Extracted Text Preview */}
          {result.pages_preview?.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer text-indigo-600 hover:text-indigo-700 font-medium">
                View extracted text preview
              </summary>
              <div className="mt-2 bg-slate-50 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs text-slate-600 whitespace-pre-wrap">
                {result.pages_preview.map(p => `--- Page ${p.page} ---\n${p.text}\n\n`).join('')}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Editable Lessons Table */}
      {editableLessons.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-700">Lessons to Import ({editableLessons.length})</h3>
            <div className="flex gap-2">
              <button onClick={addEmptyLesson} className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add Row
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 text-slate-600 font-medium">Service Type</th>
                    <th className="text-left px-3 py-2 text-slate-600 font-medium">Date</th>
                    <th className="text-left px-3 py-2 text-slate-600 font-medium">Bible Lesson</th>
                    <th className="px-2 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {editableLessons.map((lesson, idx) => (
                    <tr key={lesson.id || `lesson-${idx}`} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">
                        <select
                          value={lesson.service_type}
                          onChange={(e) => updateLesson(idx, 'service_type', e.target.value)}
                          className="w-full px-2 py-1 border border-slate-200 rounded text-xs"
                        >
                          {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="date"
                          value={lesson.date}
                          onChange={(e) => updateLesson(idx, 'date', e.target.value)}
                          className="w-full px-2 py-1 border border-slate-200 rounded text-xs"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          type="text"
                          value={lesson.bible_lesson}
                          onChange={(e) => updateLesson(idx, 'bible_lesson', e.target.value)}
                          placeholder="e.g. Romans 8:1-11, Psalm 23"
                          className="w-full px-2 py-1 border border-slate-200 rounded text-xs"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <button onClick={() => removeLesson(idx)} className="p-1 text-red-400 hover:text-red-600 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <button
            data-testid="import-lessons-btn"
            onClick={handleImport}
            disabled={importing || editableLessons.length === 0}
            className="w-full py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {importing ? 'Importing...' : `Import ${editableLessons.length} Lessons`}
          </button>

          {importResult && (
            <div className="p-3 bg-green-50 text-green-700 rounded-xl flex items-center gap-2" data-testid="import-result">
              <CheckCircle2 className="w-5 h-5" />
              <span>Imported {importResult.imported} lessons ({importResult.skipped} skipped, {importResult.errors?.length || 0} errors)</span>
            </div>
          )}
        </div>
      )}

      {/* Manual paste info */}
      {result && editableLessons.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">No lessons auto-parsed from this PDF</p>
            <p className="text-sm text-amber-600 mt-1">The PDF format may not match our parser. You can:</p>
            <ul className="text-sm text-amber-600 list-disc ml-4 mt-1">
              <li>Add lessons manually using the "Add Lessons" tab</li>
              <li>Click "Add Row" below to manually enter lessons from the extracted text above</li>
            </ul>
            <button onClick={addEmptyLesson} className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Start adding manually
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MANUAL ENTRY PANEL ====================
const ManualEntryPanel = ({ onLessonAdded }) => {
  const [lessons, setLessons] = useState([
    { service_type: 'Sunday Morning Service', date: '', bible_lesson: '' }
  ]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const addRow = () => {
    setLessons([...lessons, { service_type: 'Sunday Morning Service', date: '', bible_lesson: '' }]);
  };

  const updateRow = (idx, field, value) => {
    const updated = [...lessons];
    updated[idx] = { ...updated[idx], [field]: value };
    setLessons(updated);
  };

  const removeRow = (idx) => {
    if (lessons.length === 1) return;
    setLessons(lessons.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    const valid = lessons.filter(l => l.date && l.bible_lesson.trim());
    if (valid.length === 0) {
      alert('Please fill in at least one lesson with a date and Bible reference');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/bible-lessons-manage/bulk-import`, { lessons: valid });
      setResult(res.data);
      onLessonAdded();
      // Reset
      setLessons([{ service_type: 'Sunday Morning Service', date: '', bible_lesson: '' }]);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save lessons');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Manually add Bible lessons. Fill in the service type, date, and scripture references.</p>

      <div className="space-y-3">
        {lessons.map((lesson, idx) => (
          <div key={lesson.id || `manual-${idx}`} className="flex gap-2 items-start bg-white p-3 rounded-xl border border-slate-200">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <select
                data-testid={`manual-service-type-${idx}`}
                value={lesson.service_type}
                onChange={(e) => updateRow(idx, 'service_type', e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input
                type="date"
                data-testid={`manual-date-${idx}`}
                value={lesson.date}
                onChange={(e) => updateRow(idx, 'date', e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
              <input
                type="text"
                data-testid={`manual-lesson-${idx}`}
                value={lesson.bible_lesson}
                onChange={(e) => updateRow(idx, 'bible_lesson', e.target.value)}
                placeholder="e.g. Romans 8:1-11, Psalm 23:1-6"
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            <button
              onClick={() => removeRow(idx)}
              disabled={lessons.length === 1}
              className="p-2 text-red-400 hover:text-red-600 disabled:opacity-30"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <button onClick={addRow} className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add Another Lesson
        </button>
        <button
          data-testid="save-manual-lessons-btn"
          onClick={handleSubmit}
          disabled={saving}
          className="px-6 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Lessons'}
        </button>
      </div>

      {result && (
        <div className="p-3 bg-green-50 text-green-700 rounded-xl flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" />
          <span>Saved {result.imported} lessons successfully</span>
        </div>
      )}
    </div>
  );
};

// ==================== BROWSE LESSONS PANEL ====================
const BrowseLessonsPanel = ({ lessons, onDelete }) => {
  const [filter, setFilter] = useState('all');
  const [expandedDate, setExpandedDate] = useState(null);

  const filteredLessons = filter === 'all'
    ? lessons
    : lessons.filter(l => l.service_type === filter);

  // Group by date
  const grouped = filteredLessons.reduce((acc, lesson) => {
    if (!acc[lesson.date]) acc[lesson.date] = [];
    acc[lesson.date].push(lesson);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort();

  const handleDelete = async (serviceType, date) => {
    if (!window.confirm(`Delete ${serviceType} lesson for ${date}?`)) return;
    try {
      await axios.delete(`${API}/api/bible-lessons-manage/by-service/${encodeURIComponent(serviceType)}/${date}`);
      onDelete();
    } catch (error) {
      alert('Failed to delete');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-slate-500">Filter:</span>
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${filter === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}
        >
          All ({lessons.length})
        </button>
        {SERVICE_TYPES.map(t => {
          const count = lessons.filter(l => l.service_type === t).length;
          if (count === 0) return null;
          return (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-3 py-1 rounded-full text-xs font-medium ${filter === t ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}
            >
              {t.replace(' Service', '')} ({count})
            </button>
          );
        })}
      </div>

      {/* Grouped lessons */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {sortedDates.length === 0 && (
          <p className="text-center text-slate-400 py-8">No upcoming lessons found</p>
        )}
        {sortedDates.map(date => (
          <div key={date} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <button
              onClick={() => setExpandedDate(expandedDate === date ? null : date)}
              className="w-full flex items-center justify-between p-3 hover:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-indigo-500" />
                <span className="text-sm font-medium text-slate-700">{formatDate(date)}</span>
                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                  {grouped[date].length} lesson{grouped[date].length !== 1 ? 's' : ''}
                </span>
              </div>
              {expandedDate === date ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedDate === date && (
              <div className="border-t border-slate-100 divide-y divide-slate-50">
                {grouped[date].map((lesson, idx) => (
                  <div key={lesson.id || `${date}-${lesson.service_type}-${idx}`} className="px-3 py-2 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-indigo-600 font-medium">{lesson.service_type}</p>
                      <p className="text-sm text-slate-700">{lesson.bible_lesson}</p>
                      <p className="text-[10px] text-slate-400">Source: {lesson.source}</p>
                    </div>
                    <button
                      onClick={() => handleDelete(lesson.service_type, lesson.date)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BibleLessonManagement;
